// iiif.models.ts
export interface IIIFManifest {
    '@context'?: string;
    '@id': string;
    protocol?: string;
    width: number;
    height: number;
    profile?: string[];
    tiles?: Array<{
      width: number;
      height?: number;
      scaleFactors: number[];
    }>;
    sizes?: Array<{
      width: number;
      height: number;
    }>;
  }
  
  export class IIIFImage {
    baseUrl: string;
    manifestUrl: string;
    manifest?: IIIFManifest;
    
    // Alias for baseUrl for backward compatibility
    get url(): string {
      return this.baseUrl;
    }
    
    constructor(baseUrl: string) {
      // Remove trailing slash if present
      this.baseUrl = baseUrl.replace(/\/$/, '');
      this.manifestUrl = `${this.baseUrl}/info.json`;
    }
    
    get width(): number {
      return this.manifest?.width || 0;
    }
    
    get height(): number {
      return this.manifest?.height || 0;
    }
    
    get tileWidth(): number {
      return this.manifest?.tiles?.[0]?.width || 256;
    }
    
    get tileHeight(): number {
      return this.manifest?.tiles?.[0]?.height || this.manifest?.tiles?.[0]?.width || 256;
    }
    
    get scaleFactors(): number[] {
      return this.manifest?.tiles?.[0]?.scaleFactors || [1];
    }
    
    // Get a simple scaled image URL with optional format
    getFullUrl(scaleFactor: number = 100, format: string = 'jpg'): string {
      const size = scaleFactor === 100 ? 'full' : `pct:${scaleFactor}`;
      return `${this.baseUrl}/full/${size}/0/default.${format}`;
    }
    
    // Get optimized scale factor based on target dimensions
    getOptimizedScaleFactor(targetWidth: number, targetHeight: number): number {
      if (!this.manifest) return 100;
      
      // Calculate scale needed to fit within target dimensions
      const widthScale = (targetWidth / this.width) * 100;
      const heightScale = (targetHeight / this.height) * 100;
      const optimalScale = Math.min(widthScale, heightScale, 100);
      
      // If manifest has predefined scale factors, find the closest one
      if (this.scaleFactors.length > 1) {
        let closest = 100;
        let minDiff = Math.abs(100 - optimalScale);
        
        for (const factor of this.scaleFactors) {
          const percentScale = (1 / factor) * 100;
          const diff = Math.abs(percentScale - optimalScale);
          if (diff < minDiff) {
            minDiff = diff;
            closest = percentScale;
          }
        }
        
        return closest;
      }
      
      return Math.round(optimalScale);
    }
    
    // Get a tile URL using IIIF Image API 2.1 syntax
    getTileUrl(x: number, y: number, width: number, height: number, scaleFactor: number = 1): string {
      // For IIIF, the region is specified as x,y,w,h
      const region = `${x},${y},${width},${height}`;
      
      // Size can be specified as w,h or as a scale factor
      // Using exact pixel dimensions for tiles
      const size = `${Math.round(width / scaleFactor)},${Math.round(height / scaleFactor)}`;
      
      return `${this.baseUrl}/${region}/${size}/0/default.jpg`;
    }
    
    // Alternative method that some IIIF servers prefer
    getScaledAndCroppedUrl(scaleFactor: number, x: number, y: number, width: number, height: number): string {
      const region = `${x},${y},${width},${height}`;
      const size = scaleFactor === 100 ? 'full' : `pct:${scaleFactor}`;
      return `${this.baseUrl}/${region}/${size}/0/default.jpg`;
    }
  }